package org.example.project.dashboard

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.service.GlobalManager
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.MobileAnsicht
import org.example.project.service.MobileAnsichtenApi
import org.example.project.manager.NodeScope
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.Element
import org.w3c.dom.events.Event
import kotlin.math.ceil

private const val MOBILE_VIEW_ROW_GRID = "170px 72px 88px 60px"
private const val MOBILE_VIEW_MIN_COL_W = 410
private const val MOBILE_VIEW_BLOCK_GAP = 32

@Composable
fun DashboardMobileAnsichten(art: String) {
    Style(cssRules = SharedWorkspaceStyle.cssRules)
    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    val mobileNode = if (art == NodeScope.SYS) DashboardNode.SYS_MOBILE else DashboardNode.CUS_MOBILE
    val inventory = remember(mobileNode) {
        ScreenManager.mobileScreenKeys(mobileNode)
            .sortedBy { ScreenManager.labelForKey(it).lowercase() }
    }
    var settings by remember { mutableStateOf<Map<String, MobileAnsicht>>(emptyMap()) }
    var search by remember { mutableStateOf("") }
    var numCols by remember { mutableStateOf(1) }
    var containerEl by remember { mutableStateOf<Element?>(null) }

    fun measure() {
        val width = containerEl?.getBoundingClientRect()?.width ?: return
        if (width > 0) numCols = ((width + MOBILE_VIEW_BLOCK_GAP) / (MOBILE_VIEW_MIN_COL_W + MOBILE_VIEW_BLOCK_GAP)).toInt().coerceIn(1, 8)
    }

    suspend fun reload() {
        settings = MobileAnsichtenApi.all().associateBy { it.screenKey }
    }

    fun setting(key: String): MobileAnsicht = settings[key] ?: MobileAnsicht(screenKey = key)
    fun change(key: String, mode: String, enabled: Boolean) {
        val nextMode = if (enabled) mode else "AUS"
        settings = settings + (key to MobileAnsicht(screenKey = key, mode = nextMode))
    }

    LaunchedEffect(Unit) { reload() }
    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { measure() }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    val visible = inventory.filter { key ->
        search.isBlank() || ScreenManager.labelForKey(key).contains(search, true) || key.contains(search, true)
    }
    val columns = if (visible.isEmpty()) listOf(emptyList())
    else visible.chunked(ceil(visible.size / numCols.toDouble()).toInt())

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({ classes(SharedWorkspaceStyle.contentInner); style { maxWidth(100.percent); overflowY("auto"); flex(1); minHeight(0.px) } }) {
                if (internalTitleVisible) {
                    Div({ classes(SharedWorkspaceStyle.rightTitle) }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Mobile-Ansichten") }
                    }
                }
                Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center); gap(22.px); flexWrap(FlexWrap.Wrap) } }) {
                    Input(InputType.Text) {
                        classes(SharedWorkspaceStyle.textInput)
                        value(search)
                        placeholder("Mobile-Screen suchen...")
                        onInput { search = it.value }
                        style { width(220.px) }
                    }
                    LumaticaButton(
                        label = "Speichern",
                        onClick = {
                            scope.launch {
                                val saved = MobileAnsichtenApi.save(inventory.map { setting(it) })
                                settings = saved.associateBy { it.screenKey }
                                GlobalManager.triggerSessionConfigReload()
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 42
                    )
                    LumaticaButton("Refresh", { scope.launch { reload() } }, LumaticaButtonVariant.Standard, heightPx = 42)
                }
                Div({ style { alignSelf("center"); width(600.px); maxWidth(100.percent); height(1.px); backgroundColor(rgba(255,255,255,0.14)); marginTop(40.px); marginBottom(40.px) } })
                Div({
                    style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); width(100.percent); paddingBottom(40.px) }
                    ref { element -> containerEl = element; window.requestAnimationFrame { measure() }; onDispose { containerEl = null } }
                }) {
                    Div({ style { display(DisplayStyle.Grid); gridTemplateColumns("repeat($numCols, ${MOBILE_VIEW_MIN_COL_W}px)"); gap(MOBILE_VIEW_BLOCK_GAP.px); justifyContent(JustifyContent.Center); boxSizing("border-box") } }) {
                        columns.forEachIndexed { index, entries ->
                            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); minWidth(0.px); if (index < columns.lastIndex) { property("border-right", "1px solid rgba(255,255,255,0.07)"); paddingRight(20.px) } } }) {
                                Div({ classes(SharedWorkspaceStyle.permissionHeader); style { gridTemplateColumns(MOBILE_VIEW_ROW_GRID) } }) {
                                    Span { Text("Screenname") }; Span { Text("Mobile") }; Span { Text("Companion") }; Span { Text("Beide") }
                                }
                                entries.forEach { key ->
                                    val mode = setting(key).mode.uppercase()
                                    Div({ classes(SharedWorkspaceStyle.permissionRow); style { gridTemplateColumns(MOBILE_VIEW_ROW_GRID) } }) {
                                        Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text(ScreenManager.labelForKey(key)) }
                                        LumaticaCheckbox(mode == "MOBILE", { change(key, "MOBILE", it) })
                                        LumaticaCheckbox(mode == "COMPANION", { change(key, "COMPANION", it) })
                                        LumaticaCheckbox(mode == "BEIDE", { change(key, "BEIDE", it) })
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
